/**
 * Accueil – grille de cases façon iOS (mode Personnaliser) : la carte saisie suit le pointeur, les autres s'écartent
 * en direct (animation FLIP, supprimée si « réduire les animations ») ; l'ordre est enregistré quand on la pose.
 *  - souris : glisser la carte n'importe où (hors boutons et liens) ;
 *  - tactile : appui long puis glisser (un glissement rapide fait défiler la page) ;
 *  - clavier : Espace ou Entrée sur la poignée pour saisir, flèches pour déplacer, Espace ou Entrée pour poser,
 *    Échap pour annuler ; chaque étape est annoncée aux lecteurs d'écran (`annoncer`).
 * Cases : enfants directs `[data-carte]` de la grille, contenant `.agx-case-contenu` (qui suit le pointeur).
 * Hauteur des cases : nombre entier de rangées couvrant le contenu de la carte (`useHauteursCases`).
 */
import React from 'react';
import { cx, zoomRacine } from '../../design';

export type EtapeDeplacement = 'saisie' | 'position' | 'posee' | 'annulee';

interface Options<T extends string> {
  ordre: readonly T[];
  actif: boolean;
  enregistrer: (ordre: T[]) => void;
  annoncer: (etape: EtapeDeplacement, id: T, position: number, total: number) => void;
}

interface Saisie<T extends string> { id: T; clavier: boolean }

interface Geste {
  id: string; pointeur: number; tactile: boolean; x0: number; y0: number; x: number; y: number;
  decalX: number; decalY: number; demarre: boolean; minuteur?: number; image?: number; derniereCible?: string;
}

const APPUI_LONG_MS = 300;
const SEUIL_SOURIS = 4;
const SEUIL_TACTILE = 8;
const MARGE_HAUT = 72;
const MARGE_BAS = 128;   // le dock recouvre le bas de l'écran
const VITESSE_DEFILEMENT = 14;
const INTERACTIF = 'a, button:not(.ag-ts-grip), input, select, textarea, [role="radio"], .agx-widget-pop, .ag-menu';

const animationsReduites = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Position de mise en page d'une case dans la grille, sans les transformations en cours. */
function cadre(el: HTMLElement) {
  return { x: el.offsetLeft, y: el.offsetTop, l: el.offsetWidth, h: el.offsetHeight };
}

function parentDefilant(el: HTMLElement | null): HTMLElement | null {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

function creerMoteur<T extends string>(
  grille: React.RefObject<HTMLDivElement | null>,
  setProvisoire: (o: T[] | null) => void,
  setSaisie: (s: Saisie<T> | null) => void,
) {
  const courant = { options: null as unknown as Options<T>, ordre: [] as T[], saisie: null as Saisie<T> | null };
  let geste: Geste | null = null;
  let avant: Map<string, { x: number; y: number }> | null = null;
  let retour: string | null = null;      // carte posée : son contenu revient à sa case
  let focus: string | null = null;       // poignée à refocaliser après un rendu (déplacement au clavier)

  const cases = () => [...(grille.current?.querySelectorAll<HTMLElement>(':scope > [data-carte]') ?? [])];
  const caseDe = (id: string) => cases().find((c) => c.dataset.carte === id);
  const position = (id: string, ordre = courant.ordre) => ordre.indexOf(id as T) + 1;

  /** Mémorise les positions avant un changement d'ordre (animation FLIP au rendu suivant). */
  const memoriser = () => { avant = new Map(cases().map((c) => [c.dataset.carte!, cadre(c)])); };

  const deplacerVers = (id: string, cible: number) => {
    const liste = [...courant.ordre];
    const de = liste.indexOf(id as T);
    if (de < 0 || cible < 0 || cible >= liste.length || cible === de) return false;
    memoriser();
    liste.splice(de, 1);
    liste.splice(cible, 0, id as T);
    courant.ordre = liste;
    setProvisoire(liste);
    return true;
  };

  /** La carte saisie suit le pointeur (son contenu se décale ; la case reste à sa place et montre l'emplacement). */
  const suivre = () => {
    const el = geste && caseDe(geste.id), contenu = el?.querySelector<HTMLElement>(':scope > .agx-case-contenu');
    const r = grille.current?.getBoundingClientRect();
    if (!geste || !el || !contenu || !r) return;
    // Pointeur et grille mesurés à l'échelle de l'écran, cases à celle de la page (zoom de l'affichage).
    const k = cadre(el), z = zoomRacine();
    contenu.style.transform = `translate(${(geste.x - r.left - geste.decalX) / z - k.x}px, ${(geste.y - r.top - geste.decalY) / z - k.y}px)`;
  };

  /** La carte saisie prend la place de la carte survolée (ou la dernière place, sous la grille). */
  const cibler = () => {
    const r = grille.current?.getBoundingClientRect();
    if (!geste || !r) return;
    const z = zoomRacine(), x = (geste.x - r.left) / z, y = (geste.y - r.top) / z;
    const liste = cases();
    const dessous = liste.find((c) => { const k = cadre(c); return x >= k.x && x < k.x + k.l && y >= k.y && y < k.y + k.h; });
    const id = dessous?.dataset.carte;
    if (!id) {
      const bas = Math.max(0, ...liste.map((c) => { const k = cadre(c); return k.y + k.h; }));
      if (y > bas && geste.derniereCible !== '') { geste.derniereCible = ''; deplacerVers(geste.id, courant.ordre.length - 1); }
      return;
    }
    if (id === geste.id) { geste.derniereCible = undefined; return; }
    if (id === geste.derniereCible) return;   // évite l'aller-retour tant que le pointeur reste sur la même carte
    geste.derniereCible = id;
    deplacerVers(geste.id, courant.ordre.indexOf(id as T));
  };

  const defiler = () => {
    if (!geste?.demarre) return;
    const p = parentDefilant(grille.current);
    if (p) {
      const r = p.getBoundingClientRect();
      const d = geste.y < r.top + MARGE_HAUT ? -VITESSE_DEFILEMENT : geste.y > r.bottom - MARGE_BAS ? VITESSE_DEFILEMENT : 0;
      const debut = p.scrollTop;
      if (d) p.scrollTop += d;
      if (p.scrollTop !== debut) { suivre(); cibler(); }
    }
    geste.image = requestAnimationFrame(defiler);
  };

  const arreterGeste = () => {
    if (geste?.minuteur) window.clearTimeout(geste.minuteur);
    if (geste?.image) cancelAnimationFrame(geste.image);
    geste = null;
  };

  const demarrer = () => {
    const el = geste && caseDe(geste.id);
    if (!geste || !el) return;
    const r = el.getBoundingClientRect();
    geste.decalX = geste.x0 - r.left;
    geste.decalY = geste.y0 - r.top;
    geste.demarre = true;
    courant.ordre = [...courant.options.ordre];
    setSaisie({ id: geste.id as T, clavier: false });
    setProvisoire(courant.ordre);
    courant.options.annoncer('saisie', geste.id as T, position(geste.id), courant.ordre.length);
    suivre();
    geste.image = requestAnimationFrame(defiler);
  };

  /** Pose la carte saisie (`garder` = nouvel ordre enregistré, sinon retour à l'ordre initial). */
  const poser = (garder: boolean, refocaliser = true) => {
    const id = geste?.demarre ? geste.id : courant.saisie?.id;
    const clavier = !!courant.saisie?.clavier;
    arreterGeste();
    if (!id) return;
    const initial = courant.options.ordre, final = garder ? courant.ordre : [...initial];
    if (garder && final.join() !== initial.join()) courant.options.enregistrer(final);
    if (!garder) memoriser();
    retour = id;
    if (clavier && refocaliser) focus = id;
    courant.ordre = final;
    setSaisie(null);
    setProvisoire(null);
    courant.options.annoncer(garder ? 'posee' : 'annulee', id as T, position(id, final), final.length);
  };

  const cibleClavier = (id: string, touche: string): number | undefined => {
    const liste = cases(), el = caseDe(id);
    if (!el) return undefined;
    const rtl = getComputedStyle(grille.current!).direction === 'rtl';
    const i = courant.ordre.indexOf(id as T);
    if (touche === 'ArrowLeft' || touche === 'ArrowRight') return i + ((touche === 'ArrowRight') !== rtl ? 1 : -1);
    const bas = touche === 'ArrowDown', k = cadre(el), milieu = k.x + k.l / 2;
    let meilleure: string | undefined, score = Infinity;
    for (const c of liste) {
      if (c === el) continue;
      const q = cadre(c), ecart = bas ? q.y - (k.y + k.h) : k.y - (q.y + q.h);
      if (ecart < -1) continue;   // ni entièrement dessous, ni entièrement dessus
      const s = ecart * 4 + Math.abs(q.x + q.l / 2 - milieu);
      if (s < score) { score = s; meilleure = c.dataset.carte; }
    }
    return meilleure ? courant.ordre.indexOf(meilleure as T) : undefined;
  };

  return {
    maj(options: Options<T>, ordre: T[], saisie: Saisie<T> | null) {
      courant.options = options;
      courant.ordre = ordre;
      courant.saisie = saisie;
    },

    /** Après chaque rendu : animation des cases déplacées, carte saisie sous le pointeur, focus clavier. */
    apresRendu() {
      const prec = avant;
      avant = null;
      if (prec && !animationsReduites()) {
        for (const c of cases()) {
          const id = c.dataset.carte!, a = prec.get(id);
          if (!a || (geste?.demarre && id === geste.id)) continue;
          const k = cadre(c), dx = a.x - k.x, dy = a.y - k.y;
          if (!dx && !dy) continue;
          c.style.transition = 'none';
          c.style.transform = `translate(${dx}px, ${dy}px)`;
          void c.offsetWidth;
          c.style.transition = '';
          c.style.transform = '';
        }
      }
      if (geste?.demarre) suivre();
      if (retour) {
        const contenu = caseDe(retour)?.querySelector<HTMLElement>(':scope > .agx-case-contenu');
        if (contenu) contenu.style.transform = '';
        retour = null;
      }
      const f = courant.saisie?.clavier ? courant.saisie.id : focus;
      if (f) {
        const poignee = caseDe(f)?.querySelector<HTMLElement>('.ag-ts-grip');
        if (poignee && document.activeElement !== poignee) poignee.focus();
        focus = null;
      }
    },

    /** Écoute du pointeur sur la page entière pendant le mode Personnaliser. */
    ecouter(actif: boolean) {
      if (!actif) return undefined;
      const bouge = (e: PointerEvent) => {
        if (!geste || e.pointerId !== geste.pointeur) return;
        geste.x = e.clientX;
        geste.y = e.clientY;
        if (!geste.demarre) {
          const d = Math.hypot(geste.x - geste.x0, geste.y - geste.y0);
          if (geste.tactile) { if (d > SEUIL_TACTILE) arreterGeste(); } else if (d > SEUIL_SOURIS) demarrer();
          return;
        }
        suivre();
        cibler();
      };
      const fin = (e: PointerEvent) => {
        if (!geste || e.pointerId !== geste.pointeur) return;
        if (geste.demarre) poser(e.type === 'pointerup'); else arreterGeste();
      };
      const appui = (e: PointerEvent) => {
        // carte saisie au clavier : un clic ailleurs la pose
        const s = courant.saisie;
        if (s?.clavier && !caseDe(s.id)?.contains(e.target as Node)) poser(true, false);
      };
      const toucher = (e: TouchEvent) => { if (geste?.demarre && e.cancelable) e.preventDefault(); };
      const menu = (e: Event) => { if (geste) e.preventDefault(); };
      window.addEventListener('pointermove', bouge);
      window.addEventListener('pointerup', fin);
      window.addEventListener('pointercancel', fin);
      window.addEventListener('pointerdown', appui, true);
      window.addEventListener('touchmove', toucher, { passive: false });
      window.addEventListener('contextmenu', menu);
      return () => {
        if (geste?.demarre || courant.saisie) poser(false, false);
        arreterGeste();
        window.removeEventListener('pointermove', bouge);
        window.removeEventListener('pointerup', fin);
        window.removeEventListener('pointercancel', fin);
        window.removeEventListener('pointerdown', appui, true);
        window.removeEventListener('touchmove', toucher);
        window.removeEventListener('contextmenu', menu);
      };
    },

    pointeur(id: T, e: React.PointerEvent<HTMLElement>) {
      if (!courant.options.actif || geste || courant.saisie) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if ((e.target as HTMLElement).closest(INTERACTIF)) return;
      const tactile = e.pointerType !== 'mouse';
      geste = { id, pointeur: e.pointerId, tactile, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, decalX: 0, decalY: 0, demarre: false };
      if (tactile) geste.minuteur = window.setTimeout(demarrer, APPUI_LONG_MS);
      else e.preventDefault();   // pas de sélection de texte pendant le glisser
    },

    clavier(id: T, e: React.KeyboardEvent<HTMLElement>) {
      if (!courant.options.actif) return;
      const s = courant.saisie;
      if (!s) {
        if ((e.key === ' ' || e.key === 'Enter') && (e.target as HTMLElement).closest('.ag-ts-grip')) {
          e.preventDefault();
          courant.ordre = [...courant.options.ordre];
          setSaisie({ id, clavier: true });
          setProvisoire(courant.ordre);
          courant.options.annoncer('saisie', id, position(id), courant.ordre.length);
        }
        return;
      }
      if (!s.clavier || s.id !== id) return;
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const cible = cibleClavier(id, e.key);
        if (cible !== undefined && deplacerVers(id, cible)) courant.options.annoncer('position', id, cible + 1, courant.ordre.length);
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        poser(true);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        poser(false);
      } else if (e.key === 'Tab') {
        poser(true, false);
      }
    },
  };
}

export function useGrilleTriable<T extends string>(o: Options<T>) {
  const grille = React.useRef<HTMLDivElement>(null);
  const [provisoire, setProvisoire] = React.useState<T[] | null>(null);
  const [saisie, setSaisie] = React.useState<Saisie<T> | null>(null);
  const [moteur] = React.useState(() => creerMoteur<T>(grille, setProvisoire, setSaisie));
  const ordre = provisoire ?? [...o.ordre];
  moteur.maj(o, ordre, saisie);
  React.useLayoutEffect(() => moteur.apresRendu());
  React.useEffect(() => moteur.ecouter(o.actif), [moteur, o.actif]);

  const propsCase = (id: T) => ({
    'data-carte': id,
    className: cx('agx-case', saisie?.id === id && (saisie.clavier ? 'agx-case-levee' : 'agx-case-saisie')),
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => moteur.pointeur(id, e),
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => moteur.clavier(id, e),
  });
  return { grille, ordre, saisie, propsCase };
}

function lireColonnes() {
  return { colonnes: window.matchMedia('(max-width: 1100px)').matches ? 2 : 4, mobile: window.matchMedia('(max-width: 640px)').matches };
}

/** Nombre de colonnes de la grille (mêmes seuils que .ag-home-grid) : 4 sur bureau, 2 sur tablette et téléphone. */
export function useColonnes() {
  const [v, setV] = React.useState(lireColonnes);
  React.useEffect(() => {
    const requetes = [window.matchMedia('(max-width: 1100px)'), window.matchMedia('(max-width: 640px)')];
    const maj = () => setV(lireColonnes());
    requetes.forEach((q) => q.addEventListener('change', maj));
    return () => requetes.forEach((q) => q.removeEventListener('change', maj));
  }, []);
  return v;
}

/**
 * Hauteur en rangées des cases `[data-mesure]` : le plus petit nombre de cases qui contient la carte entière
 * (en-tête + contenu), recalculé quand le contenu ou la largeur change. La carte remplit ensuite toute sa case.
 */
export function useHauteursCases(grille: React.RefObject<HTMLDivElement | null>) {
  const [hauteurs, setHauteurs] = React.useState<Record<string, number>>({});
  const observateur = React.useRef<ResizeObserver | null>(null);
  React.useLayoutEffect(() => {
    const mesurer = () => {
      const g = grille.current;
      // page en cache (volet inactif, content-visibility) : pas de lecture de mise en page, mesure au retour
      if (!g || g.closest('.agx-volet-cache') || !g.offsetParent) return;
      const style = getComputedStyle(g), rangee = parseFloat(style.gridAutoRows), ecart = parseFloat(style.rowGap) || 0;
      if (!rangee) return;
      const n: Record<string, number> = {};
      g.querySelectorAll<HTMLElement>(':scope > [data-mesure]').forEach((c) => {
        const carte = c.querySelector<HTMLElement>('.ag-widget'), corps = carte?.querySelector<HTMLElement>(':scope > .ag-widget-body');
        if (!carte || !corps) return;
        const naturelle = corps.offsetTop + corps.offsetHeight + parseFloat(getComputedStyle(carte).paddingBottom);
        n[c.dataset.carte!] = Math.max(1, Math.ceil((naturelle + ecart) / (rangee + ecart) - 0.02));
      });
      setHauteurs((p) => (JSON.stringify(p) === JSON.stringify(n) ? p : n));
    };
    observateur.current ??= new ResizeObserver(mesurer);
    grille.current?.querySelectorAll<HTMLElement>(':scope > [data-mesure] .ag-widget > :is(.ag-widget-head, .ag-widget-body)')
      .forEach((el) => observateur.current?.observe(el));
    mesurer();   // avant l'affichage : pas de saut au chargement
  });
  React.useEffect(() => () => observateur.current?.disconnect(), []);
  return hauteurs;
}
